import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ref, onValue, remove, update } from "firebase/database";
import { db } from "../../firebase";
import "./FoodVendors.css";
import FoodNavbar from "../navbar/FoodNavbar";
import { calculateDeliveryTime, isClosedToday } from './vendor';
import { useCart } from './CartContext';
import { useAuth } from '../../AuthContext';
import ProfileDropdown from './ProfileDropdown';

const DEFAULT_RESTAURANT_BANNER = "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?auto=format&fit=crop&w=800&q=80";
const DEFAULT_CHEF_BANNER = "https://images.unsplash.com/photo-1577219491135-ce391730fb2c?auto=format&fit=crop&w=800&q=80";
const DEFAULT_STORE_BANNER = "https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&w=800&q=80";

const findRawImage = (v) => {
  if (!v) return null;
  return v.imageUrl || v.image || v.logo || v.profileImage || v.avatar || v.photoURL || v.photo ||
         v.chefProfile?.imageUrl || v.chefProfile?.image || v.chefProfile?.logo || v.chefProfile?.photoURL || v.chefProfile?.avatar ||
         v.vendorProfile?.imageUrl || v.vendorProfile?.image || v.vendorProfile?.logo ||
         v.profile?.imageUrl || v.profile?.image || v.details?.logo ||
         v.image_url || v.photo_url || v.vendorImage || v.storeLogo || null;
};

const findRawBanner = (v) => {
  if (!v) return null;
  return v.bannerUrl || v.banner || v.coverImage || v.headerImage || v.cover || v.coverPhoto ||
         v.chefProfile?.bannerUrl || v.chefProfile?.banner || v.chefProfile?.coverImage || v.chefProfile?.headerImage ||
         v.vendorProfile?.bannerUrl || v.vendorProfile?.banner || v.vendorProfile?.coverImage ||
         v.profile?.bannerUrl || v.profile?.banner ||
         v.banner_url || v.cover_image || v.header_image || null;
};

const getVendorProfileImgUrl = (v) => {
  if (!v) return `https://ui-avatars.com/api/?name=Vendor&background=111&color=fff`;
  const url = findRawImage(v);
  if (url && typeof url === 'string' && url.trim().length > 0) {
    return url;
  }
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(v.name || 'Vendor')}&background=111&color=fff`;
};

const getVendorBannerUrl = (v) => {
  if (!v) return DEFAULT_RESTAURANT_BANNER;
  const url = findRawBanner(v);
  if (url && typeof url === 'string' && url.trim().length > 0) {
    return url;
  }
  const avatar = getVendorProfileImgUrl(v);
  if (avatar && !avatar.includes('ui-avatars.com')) {
    return avatar;
  }
  if (v.category?.includes("Chefs")) return DEFAULT_CHEF_BANNER;
  if (v.category?.includes("Stores")) return DEFAULT_STORE_BANNER;
  return DEFAULT_RESTAURANT_BANNER;
};

const locationTypesList = [
  { 
    name: "House", 
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </svg>
    ) 
  },
  { 
    name: "Apartment", 
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
        <path d="M9 22v-4h6v4" />
        <path d="M8 6h.01" />
        <path d="M16 6h.01" />
        <path d="M12 6h.01" />
        <path d="M12 10h.01" />
        <path d="M12 14h.01" />
        <path d="M16 10h.01" />
        <path d="M16 14h.01" />
        <path d="M8 10h.01" />
        <path d="M8 14h.01" />
      </svg>
    ) 
  },
  { 
    name: "Office", 
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
      </svg>
    ) 
  },
  { 
    name: "Others", 
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
        <circle cx="12" cy="10" r="3" />
      </svg>
    ) 
  }
];

const categoriesList = [
  {
    name: "All",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="14" width="7" height="7" rx="1" />
        <rect x="3" y="14" width="7" height="7" rx="1" />
      </svg>
    )
  },
  {
    name: "Chefs",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 13.87A4 4 0 0 1 7.41 6a5.11 5.11 0 0 1 1.05-1.54 5 5 0 0 1 7.08 0A5.11 5.11 0 0 1 16.59 6 4 4 0 0 1 18 13.87V21H6Z" />
        <line x1="6" y1="17" x2="18" y2="17" />
      </svg>
    )
  },
  {
    name: "Restaurants",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2" />
        <path d="M7 2v20" />
        <path d="M21 15V2v0a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7" />
      </svg>
    )
  },
  {
    name: "Stores",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" />
        <line x1="3" y1="6" x2="21" y2="6" />
        <path d="M16 10a4 4 0 0 1-8 0" />
      </svg>
    )
  }
];

const filtersByCategory = {
  All: ["Discounts", "Delivery fee", "Open now", "Pickup", "Ratings", "Under 30 mins"],
  Chefs: ["Available Today", "Top Rated", "Special Offers", "Continental", "Local", "Meal Prep"],
  Restaurants: ["Discounts", "Free Delivery", "Open now", "Pickup", "Ratings", "Under 30 mins"],
  Stores: ["Groceries", "Beverages", "Open now", "Fast Delivery", "Bulk Discount"]
};

function FoodVendors() {
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useAuth();
  
  // Retrieve initial values passed from FoodRide, set up local state
  const { address: initialAddress, locationType: initialLocationType, searchQuery: initialSearchQuery, selectedLocation: initialSelectedLocation } = location.state || {};
  const [address, setAddress] = useState(initialAddress || localStorage.getItem('9troride_address') || "");
  const [locationType, setLocationType] = useState(initialLocationType || localStorage.getItem('9troride_locationType') || "");

  useEffect(() => {
    // Keep localStorage in sync with the state
    if (address) localStorage.setItem('9troride_address', address);
    if (locationType) localStorage.setItem('9troride_locationType', locationType);
  }, [address, locationType]);
  
  // Modal State
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [editAddress, setEditAddress] = useState("");
  const [editLocationType, setEditLocationType] = useState("");
  const [predictions, setPredictions] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(
    initialSelectedLocation || (localStorage.getItem('9troride_selectedLocation') ? JSON.parse(localStorage.getItem('9troride_selectedLocation')) : null)
  );
  const autocompleteService = useRef(null);
  const geocoder = useRef(null);
  const mapRef = useRef(null);
  const [touchStartY, setTouchStartY] = useState(0);
  const [touchCurrentY, setTouchCurrentY] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [activeFilters, setActiveFilters] = useState([]);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [sortOption, setSortOption] = useState("Recommended");
  const [priceFilter, setPriceFilter] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery || "");
  const [isLoadingVendors, setIsLoadingVendors] = useState(true);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const { cartItemCount, clearAllCartData } = useCart();

  const [showChefsArrows, setShowChefsArrows] = useState({ left: false, right: false });
  const [showRestaurantsArrows, setShowRestaurantsArrows] = useState({ left: false, right: false });
  const [showStoresArrows, setShowStoresArrows] = useState({ left: false, right: false });

  const chefsCarouselRef = useRef(null);
  const restaurantsCarouselRef = useRef(null);
  const storesCarouselRef = useRef(null);

  const geocodeAddress = (addressString) => {
    if (window.google && window.google.maps) {
      if (!geocoder.current) {
        geocoder.current = new window.google.maps.Geocoder();
      }
      geocoder.current.geocode({ address: addressString }, (results, status) => {
        if (status === "OK" && results[0]) {
          setSelectedLocation({
            lat: results[0].geometry.location.lat(),
            lng: results[0].geometry.location.lng()
          });
        }
      });
    }
  };

  const handleScroll = (ref, direction) => {
    if (ref.current) {
      const scrollAmount = direction === 'left' ? -300 : 300;
      ref.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  const handleOpenAddressModal = () => {
    setEditAddress(address);
    setEditLocationType(locationType);
    setPredictions([]);
    setIsAddressModalOpen(true);
    
    // Geocode the existing address to display it on the map immediately
    if (address && !selectedLocation) {
      geocodeAddress(address);
    }
  };

  const handleSaveAddress = () => {
    setAddress(editAddress);
    setLocationType(editLocationType);
    // The useEffect hook will automatically save the new address to localStorage
    setIsAddressModalOpen(false);
  };

  const handleLogout = async () => {
    try {
      setIsProfileDropdownOpen(false);
      await auth.signOut();
      if (clearAllCartData) {
        clearAllCartData();
      }
      localStorage.removeItem('9troride_address');
      localStorage.removeItem('9troride_locationType');
      sessionStorage.removeItem('9troride_checkoutVendorId');
      sessionStorage.removeItem('9troride_checkoutVendorType');
      navigate('/food');
    } catch (error) {
      console.error("Failed to log out:", error);
    }
  };

  const handleFavoriteClick = (e, vendorId) => {
    e.stopPropagation();
    if (!auth.currentUser) {
      navigate('/login', { state: { from: location } });
      return;
    }

    const userId = auth.currentUser.uid;
    const isFavorite = auth.riderData?.favorites?.[vendorId];

    if (isFavorite) {
      remove(ref(db, `favorites/${userId}/${vendorId}`));
    } else {
      const vendor = vendors.find(v => v.id === vendorId);
      if (vendor) {
        let vendorType = 'restaurant'; // Default
        if (vendor.category.includes('Chefs')) {
          vendorType = 'chef';
        } else if (vendor.category.includes('Stores')) {
          vendorType = 'store';
        }
        update(ref(db, `favorites/${userId}`), { [vendorId]: { type: vendorType } });
      }
    }
  };

  const filteredVendors = vendors.filter(v => {
    if (searchQuery.trim() && !v.searchableText.includes(searchQuery.toLowerCase().trim())) {
      return false;
    }
    
    if (activeFilters.includes("Under 30 mins")) {
      const delivery = calculateDeliveryTime(selectedLocation, v.location);
      if (!delivery.isFast) return false;
    }
    
    // Filter out if isOpen is false for that day on the database
    if (v.workingHours) {
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const dayName = days[new Date().getDay()];
      const hoursToday = v.workingHours[dayName];
      if (hoursToday && hoursToday.isOpen === false) {
        return false;
      }
    }
    
    return true;
  });

  // Fetch vendors from your database in real-time
  useEffect(() => {
    let chefsData = {};
    let vendorsData = {};
    let productsData = {};
    let loaded = { chefs: false, vendors: false, products: false };

    const processData = () => {
      if (!loaded.chefs || !loaded.vendors || !loaded.products) return;
      
        const chefs = Object.values(chefsData)
          .filter(chef => chef.status !== 'pending_approval')
          .map(chef => {
            const specialtiesArray = chef.chefProfile?.specialties?.map(s => s.name) || [];
            const specialties = specialtiesArray.join(', ') || 'Private Chef';
            const img = findRawImage(chef);
            const banner = findRawBanner(chef);
            return {
              id: chef.id,
              name: chef.name,
              chefCategory: chef.chefProfile?.category,
              category: ['Chefs'],
              description: `Specialties: ${specialties}`,
              tags: specialtiesArray.length > 0 ? specialtiesArray : ['Private Chef'],
              imageUrl: img,
              bannerUrl: banner,
              rawRecord: chef,
              searchableText: `${chef.name} ${specialties}`.toLowerCase(),
              products: specialtiesArray,
              fullProducts: chef.chefProfile?.specialties || [],
              location: chef.location,
              isVerified: chef.isVerified || chef.verified || false,
              rating: chef.rating,
              reviewsCount: chef.reviewsCount,
              workingHours: chef.workingHours,
              address: chef.address,
              phone: chef.phone,
              email: chef.email
            };
          });

        const vendorsList = Object.values(vendorsData)
          .filter(vendor => vendor.status !== 'pending_approval')
          .map(vendor => {
            const vendorProducts = Object.values(productsData).filter(p => p.vendorId === vendor.id);
          const hasMeal = vendorProducts.some(p => p.itemCategory === 'meal');
          const isStore = vendorProducts.some(p => p.itemCategory === 'item' || p.itemCategory === 'beverage');
          
          let description = '';
          let category = [];
          let tags = [];

          if (hasMeal || !isStore) {
            category.push('Restaurants');
            const productCategories = new Set();
            vendorProducts.forEach(p => {
              if (p.category) {
                p.category.forEach(cat => productCategories.add(cat));
              }
            });
            tags = Array.from(productCategories);
            description = tags.join(', ');
            if (!description) {
                description = "Variety of meals";
                tags = ["Variety of meals"];
            }
          }
          
          if (isStore) {
            category.push('Stores');
            if (category.length === 1) {
              description = 'Groceries & Essentials';
              tags = ['Groceries & Essentials'];
            } else {
              tags.push('Groceries');
            }
          }

          const img = findRawImage(vendor);
          const banner = findRawBanner(vendor);

          return {
            id: vendor.id,
            name: vendor.name,
            category: category,
            description: description,
            tags: tags,
            imageUrl: img,
            bannerUrl: banner,
            rawRecord: vendor,
            searchableText: `${vendor.name} ${description} ${vendorProducts.map(p => p.name).join(' ')}`.toLowerCase(),
            products: vendorProducts.map(p => p.name),
            fullProducts: vendorProducts,
            location: vendor.location,
            isVerified: vendor.isVerified || vendor.verified || false,
            rating: vendor.rating,
            reviewsCount: vendor.reviewsCount,
            workingHours: vendor.workingHours,
            address: vendor.address,
            phone: vendor.phone,
            email: vendor.email
          };
        });

        const allVendors = [...chefs, ...vendorsList];
        setVendors(allVendors);
        setIsLoadingVendors(false);
    };

    const unsubscribeChefs = onValue(ref(db, 'chefs'), (snapshot) => {
      chefsData = snapshot.val() || {};
      loaded.chefs = true;
      processData();
    }, (error) => console.error("Failed to fetch chefs:", error));

    const unsubscribeVendors = onValue(ref(db, 'vendors'), (snapshot) => {
      vendorsData = snapshot.val() || {};
      loaded.vendors = true;
      processData();
    }, (error) => console.error("Failed to fetch vendors:", error));

    const unsubscribeProducts = onValue(ref(db, 'products'), (snapshot) => {
      productsData = snapshot.val() || {};
      loaded.products = true;
      processData();
    }, (error) => console.error("Failed to fetch products:", error));

    return () => {
      unsubscribeChefs();
      unsubscribeVendors();
      unsubscribeProducts();
    };
  }, []);

  useEffect(() => {
    const carousels = [
      { ref: chefsCarouselRef, setter: setShowChefsArrows },
      { ref: restaurantsCarouselRef, setter: setShowRestaurantsArrows },
      { ref: storesCarouselRef, setter: setShowStoresArrows },
    ];

    const checkScrollability = (el, setter) => {
      if (el) {
        const hasOverflow = el.scrollWidth > el.clientWidth;
        setter({
          left: hasOverflow && el.scrollLeft > 5,
          right: hasOverflow && el.scrollLeft < el.scrollWidth - el.clientWidth - 5,
        });
      }
    };

    const checkAllCarousels = () => {
        carousels.forEach(({ ref, setter }) => checkScrollability(ref.current, setter));
    };

    const timer = setTimeout(checkAllCarousels, 150);
    window.addEventListener('resize', checkAllCarousels);
    
    const observersAndListeners = carousels.map(({ ref, setter }) => {
      const el = ref.current;
      const observer = new MutationObserver(() => checkScrollability(el, setter));
      const scrollListener = () => checkScrollability(el, setter);
      
      if (el) {
        observer.observe(el, { childList: true });
        el.addEventListener('scroll', scrollListener);
      }
      return { el, observer, scrollListener };
    });

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', checkAllCarousels);
      observersAndListeners.forEach(({ el, observer, scrollListener }) => {
        if (el) {
          observer.disconnect();
          el.removeEventListener('scroll', scrollListener);
        }
      });
    };
  }, [filteredVendors, selectedCategory]);

  // Initialize Google Map in the modal when opened
  useEffect(() => {
    if (isAddressModalOpen && mapRef.current && selectedLocation && window.google && window.google.maps) {
      const map = new window.google.maps.Map(mapRef.current, {
        center: selectedLocation,
        zoom: 16,
        disableDefaultUI: true,
        zoomControl: false,
        gestureHandling: "none",
        keyboardShortcuts: false,
      });

      const customIcon = {
        path: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z",
        fillColor: "#E3242B",
        fillOpacity: 1,
        strokeWeight: 2,
        strokeColor: "#ffffff",
        scale: 1.8,
        anchor: new window.google.maps.Point(12, 24),
      };

      const marker = new window.google.maps.Marker({
        position: selectedLocation,
        map: map,
        icon: customIcon,
      });

      const infoWindow = new window.google.maps.InfoWindow({
        content: `<div style="padding: 4px; text-align: center; max-width: 200px; font-family: 'Plus Jakarta Sans', sans-serif;"><div style="font-weight: 600; font-size: 14px; color: #111; margin-bottom: 6px;">${editAddress}</div><div style="font-size: 12px; color: #888; line-height: 1.4;">We are going to deliver your food here.</div></div>`,
      });

      infoWindow.open(map, marker);
    }
  }, [isAddressModalOpen, selectedLocation, editAddress]);

  const handleAddressChange = (e) => {
    const value = e.target.value;
    setEditAddress(value);

    if (!value) {
      setPredictions([]);
      return;
    }

    if (window.google && window.google.maps && window.google.maps.places) {
      if (!autocompleteService.current) {
        autocompleteService.current = new window.google.maps.places.AutocompleteService();
      }

      autocompleteService.current.getPlacePredictions(
        { input: value },
        (results, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
            setPredictions(results);
          } else {
            setPredictions([]);
          }
        }
      );
    }
  };

  const handlePredictionSelect = (prediction) => {
    setEditAddress(prediction.description);
    setPredictions([]);

    if (window.google && window.google.maps) {
      if (!geocoder.current) {
        geocoder.current = new window.google.maps.Geocoder();
      }
      geocoder.current.geocode({ placeId: prediction.place_id }, (results, status) => {
        if (status === "OK" && results[0]) {
          setSelectedLocation({
            lat: results[0].geometry.location.lat(),
            lng: results[0].geometry.location.lng()
          });
        }
      });
    }
  };

  const handleTouchStart = (e) => {
    setTouchStartY(e.touches[0].clientY);
  };

  const handleTouchMove = (e) => {
    const y = e.touches[0].clientY;
    if (y - touchStartY > 0) {
      setTouchCurrentY(y - touchStartY);
    }
  };

  const handleTouchEnd = () => {
    if (touchCurrentY > 100) {
      setIsAddressModalOpen(false);
    }
    setTouchCurrentY(0);
  };

  const toggleFilter = (filter) => {
    setActiveFilters(prev => 
      prev.includes(filter) 
        ? prev.filter(f => f !== filter)
        : [...prev, filter]
    );
  };

  const togglePrice = (price) => {
    setPriceFilter(prev => 
      prev.includes(price) 
        ? prev.filter(p => p !== price) 
        : [...prev, price]
    );
  };

  const clearFilters = () => {
    setActiveFilters([]);
    setPriceFilter([]);
    setSortOption("Recommended");
  };

  const currentFilters = filtersByCategory[selectedCategory] || filtersByCategory["All"];

  const handleCategoryChange = (catName) => {
    setSelectedCategory(catName);
    setActiveFilters([]); // Clear active filters when switching categories
  };

  return (
    <div className="food-vendors-page">
      <FoodNavbar 
        address={address}
        locationType={locationType}
        handleOpenAddressModal={handleOpenAddressModal}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}        
        cartItemCount={cartItemCount}
      />

      {/* Mobile Bottom Navigation */}
      <div className="vendor-bottom-nav">
        <button className={`bottom-nav-item ${location.pathname === '/food' ? 'active' : ''}`} onClick={() => navigate('/food')}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg>
          <span>Home</span>
        </button>
        <button className={`bottom-nav-item ${location.pathname.startsWith('/food-vendors') || location.pathname.startsWith('/restaurant') || location.pathname.startsWith('/store') || location.pathname.startsWith('/chef') ? 'active' : ''}`} onClick={() => navigate('/food-vendors')}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
          <span>Browse</span>
        </button>
        <button className={`bottom-nav-item ${location.pathname === '/favorites' ? 'active' : ''}`} onClick={() => navigate('/favorites')}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>
          <span>Favorites</span>
        </button>
        <button className={`bottom-nav-item ${location.pathname === '/checkout' ? 'active' : ''}`} onClick={() => navigate('/checkout')}>
          <div className="cart-icon-container">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="21" r="1"></circle><circle cx="20" cy="21" r="1"></circle><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path></svg>
            {cartItemCount > 0 && <span className="cart-badge-bottom-nav">{cartItemCount}</span>}
          </div>
          <span>Cart</span>
        </button>
        <button 
          className={`bottom-nav-item ${location.pathname === '/settings' ? 'active' : ''}`} 
          onClick={() => { 
            if (auth.currentUser) {
              navigate('/settings');
            } else {
              navigate('/login');
            }
          }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
          <span>{auth.currentUser ? "Profile" : "Log In"}</span>
        </button>
      </div>

      <div className="controls-row">
        <div className="category-filters">
          {categoriesList.map(cat => (
            <button 
              key={cat.name}
              className={`category-pill ${selectedCategory === cat.name ? 'active' : ''}`}
              onClick={() => handleCategoryChange(cat.name)}
            >
              {cat.icon}
              {cat.name}
            </button>
          ))}
        </div>
        <button className="master-filter-btn" onClick={() => setIsFilterModalOpen(true)}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
          </svg>
          Filters
          {(activeFilters.length > 0 || priceFilter.length > 0 || sortOption !== "Recommended") && (
            <span className="filter-badge">
              {activeFilters.length + priceFilter.length + (sortOption !== "Recommended" ? 1 : 0)}
            </span>
          )}
        </button>
      </div>

      {activeFilters.length > 0 && (
        <div className="action-filters">
          {activeFilters.map(filter => (
          <button 
            key={filter}
            className="action-pill active"
            onClick={() => toggleFilter(filter)}
          >
            {filter}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        ))}
        </div>
      )}

      <div className="vendors-content">
        {isLoadingVendors ? (
          <div style={{ textAlign: "center", padding: "40px", color: "#888" }}>Loading vendors...</div>
        ) : (
          <>
            {(selectedCategory === "All" || selectedCategory === "Chefs") && filteredVendors.filter(v => v.category.includes("Chefs")).length > 0 && (
              <div className="vendor-category">
                <h3>Private Chefs</h3>
                {selectedCategory === "All" ? (
                  <div className="carousel-wrapper">
                    {showChefsArrows.left && (
                      <button className="scroll-btn left" onClick={() => handleScroll(chefsCarouselRef, 'left')}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
                      </button>
                    )}
                    <div className="vendor-carousel" ref={chefsCarouselRef}>
                      {filteredVendors.filter(v => v.category.includes("Chefs")).map(vendor => {
                        const delivery = calculateDeliveryTime(selectedLocation, vendor.location);
                        const isClosed = isClosedToday(vendor.workingHours);
                        return (
                          <div className={`vendor-card ${isClosed ? 'closed' : ''}`} key={vendor.id} onClick={isClosed ? undefined : () => navigate(`/9troeat/chef/${vendor.id}`, { state: { vendor, selectedLocation, address, locationType } })}>
                            <div className="vendor-banner-wrapper">
                              <div className="vendor-banner" style={{ backgroundImage: `url("${getVendorBannerUrl(vendor)}")` }}></div>
                              {isClosed && (
                                <div className="vendor-lock-overlay">
                                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="lock-icon">
                                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                                  </svg>
                                  <span className="closed-label">Closed</span>
                                </div>
                              )}
                              <button className={`vendor-card-fav ${auth.riderData?.favorites?.[vendor.id] ? 'is-favorite' : ''}`} onClick={(e) => handleFavoriteClick(e, vendor.id)}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>
                              </button>
                              <div className="vendor-delivery-badge" style={{ color: delivery.isFast ? '#00b84c' : '#111' }}>
                                <span>{delivery.text}</span>
                              </div>
                              <img src={getVendorProfileImgUrl(vendor)} alt={vendor.name} className="vendor-profile-img" onError={(e) => { e.target.onerror = null; e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(vendor.name || 'Vendor')}&background=111&color=fff`; }} />
                            </div>
                            <div className="vendor-card-body">
                              <h4>
                                {vendor.name}
                                <div className="vendor-verified-badge-small" title="Verified">
                                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 1L14.75 3.06L18.11 2.72L19.31 5.89L22.3 7.15L21.1 10.32L23 13.06L20.52 15.42L20.49 18.78L17.2 19.34L15.4 22.1L12 21.05L8.6 22.1L6.8 19.34L3.51 18.78L3.48 15.42L1 13.06L2.9 10.32L1.7 7.15L4.69 5.89L5.89 2.72L9.25 3.06L12 1Z" fill="currentColor"/>
                                    <path d="M10.5 16L6.5 12L7.91 10.59L10.5 13.17L16.09 7.59L17.5 9L10.5 16Z" fill="white"/>
                                  </svg>
                                </div>
                              </h4>
                              {vendor.chefCategory && (
                                <div className="vendor-tags">
                                  <span className="vendor-tag">{vendor.chefCategory}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {showChefsArrows.right && (
                      <button className="scroll-btn right" onClick={() => handleScroll(chefsCarouselRef, 'right')}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="vendor-grid">
                    {filteredVendors.filter(v => v.category.includes("Chefs")).map(vendor => {
                      const delivery = calculateDeliveryTime(selectedLocation, vendor.location);
                      const isClosed = isClosedToday(vendor.workingHours);
                      return (
                        <div className={`vendor-card ${isClosed ? 'closed' : ''}`} key={vendor.id} onClick={isClosed ? undefined : () => navigate(`/9troeat/chef/${vendor.id}`, { state: { vendor, selectedLocation, address, locationType } })}>
                          <div className="vendor-banner-wrapper">
                              <div className="vendor-banner" style={{ backgroundImage: `url("${getVendorBannerUrl(vendor)}")` }}></div>
                            {isClosed && (
                              <div className="vendor-lock-overlay">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="lock-icon">
                                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                  <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                                </svg>
                                <span className="closed-label">Closed</span>
                              </div>
                            )}
                            <button className={`vendor-card-fav ${auth.riderData?.favorites?.[vendor.id] ? 'is-favorite' : ''}`} onClick={(e) => handleFavoriteClick(e, vendor.id)}>
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>
                            </button>
                            <div className="vendor-delivery-badge" style={{ color: delivery.isFast ? '#00b84c' : '#111' }}>
                              <span>{delivery.text}</span>
                            </div>
                            <img src={getVendorProfileImgUrl(vendor)} alt={vendor.name} className="vendor-profile-img" onError={(e) => { e.target.onerror = null; e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(vendor.name || 'Vendor')}&background=111&color=fff`; }} />
                          </div>
                          <div className="vendor-card-body">
                            <h4>
                              {vendor.name}
                              <div className="vendor-verified-badge-small" title="Verified">
                                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M12 1L14.75 3.06L18.11 2.72L19.31 5.89L22.3 7.15L21.1 10.32L23 13.06L20.52 15.42L20.49 18.78L17.2 19.34L15.4 22.1L12 21.05L8.6 22.1L6.8 19.34L3.51 18.78L3.48 15.42L1 13.06L2.9 10.32L1.7 7.15L4.69 5.89L5.89 2.72L9.25 3.06L12 1Z" fill="currentColor"/>
                                  <path d="M10.5 16L6.5 12L7.91 10.59L10.5 13.17L16.09 7.59L17.5 9L10.5 16Z" fill="white"/>
                                </svg>
                              </div>
                            </h4>
                            {vendor.chefCategory && (
                              <div className="vendor-tags">
                                <span className="vendor-tag">{vendor.chefCategory}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {(selectedCategory === "All" || selectedCategory === "Restaurants") && filteredVendors.filter(v => v.category.includes("Restaurants")).length > 0 && (
              <div className="vendor-category">
                <h3>Restaurants & Vendors</h3>
                {selectedCategory === "All" ? (
                  <div className="carousel-wrapper">
                    {showRestaurantsArrows.left && (
                      <button className="scroll-btn left" onClick={() => handleScroll(restaurantsCarouselRef, 'left')}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
                      </button>
                    )}
                    <div className="vendor-carousel" ref={restaurantsCarouselRef}>
                      {filteredVendors.filter(v => v.category.includes("Restaurants")).map(vendor => {
                        const delivery = calculateDeliveryTime(selectedLocation, vendor.location);
                        const isClosed = isClosedToday(vendor.workingHours);
                        return (
                          <div className={`vendor-card ${isClosed ? 'closed' : ''}`} key={vendor.id} onClick={isClosed ? undefined : () => navigate(`/9troeat/restaurant/${vendor.id}`, { state: { vendor, selectedLocation, address, locationType } })}>
                            <div className="vendor-banner-wrapper">
                              <div className="vendor-banner" style={{ backgroundImage: `url("${getVendorBannerUrl(vendor)}")` }}></div>
                              {isClosed && (
                                <div className="vendor-lock-overlay">
                                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="lock-icon">
                                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                                  </svg>
                                  <span className="closed-label">Closed</span>
                                </div>
                              )}
                              <button className={`vendor-card-fav ${auth.riderData?.favorites?.[vendor.id] ? 'is-favorite' : ''}`} onClick={(e) => handleFavoriteClick(e, vendor.id)}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>
                              </button>
                              <div className="vendor-delivery-badge" style={{ color: delivery.isFast ? '#00b84c' : '#111' }}>
                                <span>{delivery.text}</span>
                              </div>
                              <img src={getVendorProfileImgUrl(vendor)} alt={vendor.name} className="vendor-profile-img" onError={(e) => { e.target.onerror = null; e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(vendor.name || 'Vendor')}&background=111&color=fff`; }} />
                            </div>
                            <div className="vendor-card-body">
                              <h4>
                                {vendor.name}
                                <div className="vendor-verified-badge-small" title="Verified">
                                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 1L14.75 3.06L18.11 2.72L19.31 5.89L22.3 7.15L21.1 10.32L23 13.06L20.52 15.42L20.49 18.78L17.2 19.34L15.4 22.1L12 21.05L8.6 22.1L6.8 19.34L3.51 18.78L3.48 15.42L1 13.06L2.9 10.32L1.7 7.15L4.69 5.89L5.89 2.72L9.25 3.06L12 1Z" fill="currentColor"/>
                                    <path d="M10.5 16L6.5 12L7.91 10.59L10.5 13.17L16.09 7.59L17.5 9L10.5 16Z" fill="white"/>
                                  </svg>
                                </div>
                              </h4>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {showRestaurantsArrows.right && (
                      <button className="scroll-btn right" onClick={() => handleScroll(restaurantsCarouselRef, 'right')}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="vendor-grid">
                    {filteredVendors.filter(v => v.category.includes("Restaurants")).map(vendor => {
                      const delivery = calculateDeliveryTime(selectedLocation, vendor.location);
                      const isClosed = isClosedToday(vendor.workingHours);
                      return (
                        <div className={`vendor-card ${isClosed ? 'closed' : ''}`} key={vendor.id} onClick={isClosed ? undefined : () => navigate(`/9troeat/restaurant/${vendor.id}`, { state: { vendor, selectedLocation, address, locationType } })}>
                          <div className="vendor-banner-wrapper">
                              <div className="vendor-banner" style={{ backgroundImage: `url("${getVendorBannerUrl(vendor)}")` }}></div>
                            {isClosed && (
                              <div className="vendor-lock-overlay">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="lock-icon">
                                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                  <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                                </svg>
                                <span className="closed-label">Closed</span>
                              </div>
                            )}
                            <button className={`vendor-card-fav ${auth.riderData?.favorites?.[vendor.id] ? 'is-favorite' : ''}`} onClick={(e) => handleFavoriteClick(e, vendor.id)}>
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>
                            </button>
                            <div className="vendor-delivery-badge" style={{ color: delivery.isFast ? '#00b84c' : '#111' }}>
                              <span>{delivery.text}</span>
                            </div>
                            <img src={getVendorProfileImgUrl(vendor)} alt={vendor.name} className="vendor-profile-img" onError={(e) => { e.target.onerror = null; e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(vendor.name || 'Vendor')}&background=111&color=fff`; }} />
                          </div>
                          <div className="vendor-card-body">
                            <h4>
                              {vendor.name}
                              <div className="vendor-verified-badge-small" title="Verified">
                                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M12 1L14.75 3.06L18.11 2.72L19.31 5.89L22.3 7.15L21.1 10.32L23 13.06L20.52 15.42L20.49 18.78L17.2 19.34L15.4 22.1L12 21.05L8.6 22.1L6.8 19.34L3.51 18.78L3.48 15.42L1 13.06L2.9 10.32L1.7 7.15L4.69 5.89L5.89 2.72L9.25 3.06L12 1Z" fill="currentColor"/>
                                  <path d="M10.5 16L6.5 12L7.91 10.59L10.5 13.17L16.09 7.59L17.5 9L10.5 16Z" fill="white"/>
                                </svg>
                              </div>
                            </h4>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {(selectedCategory === "All" || selectedCategory === "Stores") && filteredVendors.filter(v => v.category.includes("Stores")).length > 0 && (
              <div className="vendor-category">
                <h3>Stores & Groceries</h3>
                {selectedCategory === "All" ? (
                  <div className="carousel-wrapper">
                    {showStoresArrows.left && (
                      <button className="scroll-btn left" onClick={() => handleScroll(storesCarouselRef, 'left')}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
                      </button>
                    )}
                    <div className="vendor-carousel" ref={storesCarouselRef}>
                      {filteredVendors.filter(v => v.category.includes("Stores")).map(vendor => {
                        const delivery = calculateDeliveryTime(selectedLocation, vendor.location);
                        const isClosed = isClosedToday(vendor.workingHours);
                        return (
                          <div className={`vendor-card ${isClosed ? 'closed' : ''}`} key={vendor.id} onClick={isClosed ? undefined : () => navigate(`/9troeat/store/${vendor.id}`, { state: { vendor, selectedLocation, address, locationType } })}>
                            <div className="vendor-banner-wrapper">
                              <div className="vendor-banner" style={{ backgroundImage: `url("${getVendorBannerUrl(vendor)}")` }}></div>
                              {isClosed && (
                                <div className="vendor-lock-overlay">
                                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="lock-icon">
                                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                                  </svg>
                                  <span className="closed-label">Closed</span>
                                </div>
                              )}
                              <button className={`vendor-card-fav ${auth.riderData?.favorites?.[vendor.id] ? 'is-favorite' : ''}`} onClick={(e) => handleFavoriteClick(e, vendor.id)}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>
                              </button>
                              <div className="vendor-delivery-badge" style={{ color: delivery.isFast ? '#00b84c' : '#111' }}>
                                <span>{delivery.text}</span>
                              </div>
                              <img src={getVendorProfileImgUrl(vendor)} alt={vendor.name} className="vendor-profile-img" onError={(e) => { e.target.onerror = null; e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(vendor.name || 'Vendor')}&background=111&color=fff`; }} />
                            </div>
                            <div className="vendor-card-body">
                              <h4>
                                {vendor.name}
                                <div className="vendor-verified-badge-small" title="Verified">
                                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 1L14.75 3.06L18.11 2.72L19.31 5.89L22.3 7.15L21.1 10.32L23 13.06L20.52 15.42L20.49 18.78L17.2 19.34L15.4 22.1L12 21.05L8.6 22.1L6.8 19.34L3.51 18.78L3.48 15.42L1 13.06L2.9 10.32L1.7 7.15L4.69 5.89L5.89 2.72L9.25 3.06L12 1Z" fill="currentColor"/>
                                    <path d="M10.5 16L6.5 12L7.91 10.59L10.5 13.17L16.09 7.59L17.5 9L10.5 16Z" fill="white"/>
                                  </svg>
                                </div>
                              </h4>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {showStoresArrows.right && (
                      <button className="scroll-btn right" onClick={() => handleScroll(storesCarouselRef, 'right')}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="vendor-grid">
                    {filteredVendors.filter(v => v.category.includes("Stores")).map(vendor => {
                      const delivery = calculateDeliveryTime(selectedLocation, vendor.location);
                      const isClosed = isClosedToday(vendor.workingHours);
                      return (
                        <div className={`vendor-card ${isClosed ? 'closed' : ''}`} key={vendor.id} onClick={isClosed ? undefined : () => navigate(`/9troeat/store/${vendor.id}`, { state: { vendor, selectedLocation, address, locationType } })}>
                          <div className="vendor-banner-wrapper">
                              <div className="vendor-banner" style={{ backgroundImage: `url("${getVendorBannerUrl(vendor)}")` }}></div>
                            {isClosed && (
                              <div className="vendor-lock-overlay">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="lock-icon">
                                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                  <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                                </svg>
                                <span className="closed-label">Closed</span>
                              </div>
                            )}
                            <button className={`vendor-card-fav ${auth.riderData?.favorites?.[vendor.id] ? 'is-favorite' : ''}`} onClick={(e) => handleFavoriteClick(e, vendor.id)}>
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>
                            </button>
                            <div className="vendor-delivery-badge" style={{ color: delivery.isFast ? '#00b84c' : '#111' }}>
                              <span>{delivery.text}</span>
                            </div>
                            <img src={getVendorProfileImgUrl(vendor)} alt={vendor.name} className="vendor-profile-img" onError={(e) => { e.target.onerror = null; e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(vendor.name || 'Vendor')}&background=111&color=fff`; }} />
                          </div>
                          <div className="vendor-card-body">
                            <h4>
                              {vendor.name}
                              <div className="vendor-verified-badge-small" title="Verified">
                                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M12 1L14.75 3.06L18.11 2.72L19.31 5.89L22.3 7.15L21.1 10.32L23 13.06L20.52 15.42L20.49 18.78L17.2 19.34L15.4 22.1L12 21.05L8.6 22.1L6.8 19.34L3.51 18.78L3.48 15.42L1 13.06L2.9 10.32L1.7 7.15L4.69 5.89L5.89 2.72L9.25 3.06L12 1Z" fill="currentColor"/>
                                  <path d="M10.5 16L6.5 12L7.91 10.59L10.5 13.17L16.09 7.59L17.5 9L10.5 16Z" fill="white"/>
                                </svg>
                              </div>
                            </h4>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {filteredVendors.length === 0 && (
              <div style={{ textAlign: "center", padding: "40px", color: "#888" }}>No chefs, restaurants, or stores matched your search.</div>
            )}
          </>
        )}
      </div>

      {isAddressModalOpen && (
        <div className="vendors-modal-overlay">
          <div 
            className="vendors-modal"
            style={touchCurrentY > 0 ? { transform: `translateY(${touchCurrentY}px)`, transition: 'none' } : {}}
          >
            <div 
              className="vendors-modal-header"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div className="mobile-drag-handle"></div>
              <div className="vendors-modal-title-row">
                <h2>Change Address</h2>
                <button className="vendors-close-btn" onClick={() => setIsAddressModalOpen(false)}>×</button>
              </div>
            </div>
            
            <div className="vendors-map-container" ref={mapRef}></div>
            
            <div className="vendors-modal-body">
              <div className="vendors-input-group">
                <label>Delivery Address</label>
                <div className="vendors-address-input-wrapper">
                  <input 
                    type="text" 
                    value={editAddress} 
                    onChange={handleAddressChange} 
                    placeholder="What's your new address?" 
                  />
                  {editAddress && (
                    <button 
                      className="vendors-clear-address-btn" 
                      onClick={() => { setEditAddress(""); setPredictions([]); }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                    </button>
                  )}
                </div>
                {predictions.length > 0 && (
                  <ul className="vendors-predictions-dropdown">
                    {predictions.map((p) => (
                      <li 
                        key={p.place_id} 
                        className="vendors-prediction-item"
                        onClick={() => handlePredictionSelect(p)}
                      >
                        {p.description}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="vendors-location-type">
                <h3>Add a label</h3>
                <div className="vendors-type-buttons">
                  {locationTypesList.map((type) => (
                    <button 
                      key={type.name}
                      className={`vendors-type-btn ${editLocationType === type.name ? 'active' : ''}`}
                      onClick={() => setEditLocationType(type.name)}
                    >
                      {type.icon}
                      {type.name}
                    </button>
                  ))}
                </div>
              </div>
              
              <button 
                className="vendors-save-btn" 
                onClick={handleSaveAddress}
                disabled={!editAddress || !editLocationType}
              >
                Save & Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {isFilterModalOpen && (
        <div className="vendors-modal-overlay">
          <div 
            className="vendors-modal pro-filter-modal"
            style={touchCurrentY > 0 ? { transform: `translateY(${touchCurrentY}px)`, transition: 'none' } : {}}
          >
            <div 
              className="vendors-modal-header pro-header"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div className="mobile-drag-handle"></div>
              <div className="vendors-modal-title-row">
                <button className="pro-header-close" onClick={() => setIsFilterModalOpen(false)}>✕</button>
                <h2>Filters</h2>
                <button className="pro-header-reset" onClick={clearFilters}>Reset</button>
              </div>
            </div>
            
            <div className="vendors-modal-body pro-body">
              <div className="pro-filter-section">
                <h3>Sort by</h3>
                <div className="pro-radio-list">
                  {['Recommended', 'Most Popular', 'Rating', 'Delivery Time'].map(opt => (
                    <label key={opt} className="pro-radio-label">
                      <span>{opt}</span>
                      <input type="radio" name="sort" checked={sortOption === opt} onChange={() => setSortOption(opt)} />
                      <div className="pro-radio-custom"></div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="pro-filter-section">
                <h3>Price Range</h3>
                <div className="pro-price-grid">
                  {['$', '$$', '$$$', '$$$$'].map(price => (
                    <button key={price} className={`pro-price-btn ${priceFilter.includes(price) ? 'active' : ''}`} onClick={() => togglePrice(price)}>{price}</button>
                  ))}
                </div>
              </div>

              <div className="pro-filter-section">
                <h3>Quick Filters</h3>
                <div className="pro-pill-grid">
                  {currentFilters.map(filter => (
                    <button key={filter} className={`pro-filter-pill ${activeFilters.includes(filter) ? 'active' : ''}`} onClick={() => toggleFilter(filter)}>
                      {filter}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="pro-modal-footer">
              <button className="vendors-save-btn" onClick={() => setIsFilterModalOpen(false)}>
                Show Results
              </button>
            </div>
          </div>
        </div>
      )}

      {isProfileDropdownOpen && (
        <ProfileDropdown
          user={auth.currentUser}
          rider={auth.riderData}
          onLogout={handleLogout}
          onClose={() => setIsProfileDropdownOpen(false)}
        />
      )}
    </div>
  );
}

export default FoodVendors;